import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, RotateCcw, XCircle } from "lucide-react";
import { useLang, loc, UI, QUIZ_POOL } from "@/lib/content";
import { scrollToId } from "@/lib/api";

export default function Quiz() {
  const { lang } = useLang();
  const [round, setRound] = useState(0);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const questions = useMemo(() => [...QUIZ_POOL].sort(() => Math.random() - 0.5).slice(0, 5), [round]);
  const q = questions[idx];

  const choose = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (i === q.correct) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= questions.length) setDone(true);
    else {
      setIdx((v) => v + 1);
      setSelected(null);
    }
  };

  const restart = () => {
    setRound((r) => r + 1);
    setIdx(0);
    setSelected(null);
    setScore(0);
    setDone(false);
  };

  return (
    <section id="simulador" className="bg-paper py-20">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          data-testid="quiz-widget"
          className="rounded-[2rem] border border-line bg-white p-6 shadow-xl sm:p-10"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">{loc(UI.quiz.overline, lang)}</p>
              <h2 className="mt-2 font-display text-3xl font-semibold text-navy sm:text-4xl">{loc(UI.quiz.title, lang)}</h2>
            </div>
            {!done && (
              <span className="rounded-full border border-line px-4 py-1.5 font-mono text-xs text-mutedtext" data-testid="quiz-counter">
                {loc(UI.quiz.question, lang)} {idx + 1} {loc(UI.quiz.of, lang)} {questions.length}
              </span>
            )}
          </div>

          <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-paperdeep">
            <motion.div
              className="h-full rounded-full bg-heritage"
              animate={{ width: `${((done ? questions.length : idx) / questions.length) * 100}%` }}
              transition={{ duration: 0.5 }}
              data-testid="quiz-progress"
            />
          </div>

          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={round + "-" + idx}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.35 }}
                className="mt-8"
              >
                <p className="font-display text-2xl font-semibold leading-snug text-navy sm:text-3xl">{loc(q.q, lang)}</p>
                <div className="mt-6 grid gap-3" data-testid="quiz-options">
                  {q.opts[lang].map((opt, i) => {
                    const isCorrect = i === q.correct;
                    const isChosen = selected === i;
                    let cls = "border-line bg-paper hover:border-navy/40 hover:bg-white";
                    if (selected !== null) {
                      if (isCorrect) cls = "border-emeraldsucc bg-emeraldsucc/10 text-emeraldsucc";
                      else if (isChosen) cls = "border-heritage bg-heritage/10 text-heritagedark";
                      else cls = "border-line bg-paper opacity-50";
                    }
                    return (
                      <button
                        key={i}
                        data-testid={`quiz-option-${i}`}
                        onClick={() => choose(i)}
                        className={`flex items-center justify-between gap-3 rounded-xl border px-5 py-4 text-left text-sm font-semibold transition-all ${cls}`}
                      >
                        {opt}
                        {selected !== null && isCorrect && <CheckCircle2 size={18} className="shrink-0" />}
                        {selected !== null && isChosen && !isCorrect && <XCircle size={18} className="shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                  <p data-testid="quiz-feedback" className="text-sm font-semibold">
                    {selected === null ? (
                      <span className="text-mutedtext">{loc(UI.quiz.hint, lang)}</span>
                    ) : selected === q.correct ? (
                      <span className="text-emeraldsucc">{loc(UI.quiz.correct, lang)}</span>
                    ) : (
                      <span className="text-heritagedark">{loc(UI.quiz.wrong, lang)}</span>
                    )}
                  </p>
                  {selected !== null && (
                    <button
                      data-testid="quiz-next"
                      onClick={next}
                      className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-paper transition-all hover:-translate-y-0.5"
                    >
                      {idx + 1 >= questions.length ? loc(UI.quiz.seeResult, lang) : loc(UI.quiz.next, lang)} <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="mt-10 pb-2 text-center"
              >
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">{loc(UI.quiz.result, lang)}</p>
                <p className="mt-4 font-display text-7xl font-semibold text-navy" data-testid="quiz-score">
                  {score}
                  <span className="text-3xl text-mutedtext">/{questions.length}</span>
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm text-inkbody">
                  {score === questions.length ? loc(UI.quiz.perfect, lang) : loc(UI.quiz.encourage, lang)}
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                  <a
                    href="#inscripcion"
                    data-testid="quiz-result-cta"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId("#inscripcion");
                    }}
                    className="rounded-full bg-heritage px-7 py-3.5 text-sm font-bold text-paper shadow-lg shadow-heritage/25 transition-all hover:-translate-y-0.5"
                  >
                    {loc(UI.quiz.cta, lang)}
                  </a>
                  <button
                    data-testid="quiz-restart"
                    onClick={restart}
                    className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3.5 text-sm font-bold text-navy hover:bg-paperdeep"
                  >
                    <RotateCcw size={15} /> {loc(UI.quiz.retry, lang)}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
